import * as React from 'react';
import propz from 'propz';
import { REGISTRATION_PERMISSION, RegistrationPermission, RegistrationStaff } from '../Registration';
import { RegistrationPermissionSelector } from './RegistrationPermissionSelector';
import { StudentPermission } from './StudentPermission';
import { LoginSession } from '../../../models/sessions';
import { StaffPermission } from './StaffPermission';
import { StaffRoleSelector } from './StaffRoleSelector';
import { ParentPermission } from './ParentPermission';
import { ParentDetails } from './ParentDetails';
import { RegistrationRequestConfirmation } from './RegistrationRequestConfirmation';

interface Props {
  permission: RegistrationPermission;
  permissionSelection?: RegistrationPermission;
  onPermissionClick: (permission: RegistrationPermission) => void;
  onStaffClick: (staff: RegistrationStaff) => void;
  loginSession: LoginSession;
  onSubmitStudentPermission: (data) => void;
  onSubmitStaffPermission: (data) => void;
  onSubmitParentPermission: (data) => void;
  onSubmitParentDetails: (data, draftStudents?: any[]) => void;
  onBackToPermissionSelector: (schoolDraft?: any) => void;
  onBackToSchoolPicker: () => void;
  onBackFromParentDetails: (draftStudents: any[]) => void;
  parentDetailsDraft?: any[];
  initialOneOffCode?: string;
  phone: string;
  isStaffRoleSelectorEnabled: boolean;
  isParentDetailsEnabled: boolean;
  isAutoLinkSuccessScreenEnabled: boolean;
  autoLinkedStudents: Array<{ studentId: string; studentName: string }>;
  staff: RegistrationStaff;
  school: any;
  isFirstStaffTrialConfirmationEnabled: boolean;
  onConfirmFirstStaffTrial: (trialModules: string[]) => void;
  onClearFirstStaffSchool: () => void;
  firstStaffTrialError?: string;
  isStudentRequestConfirmationEnabled: boolean;
  isStaffRequestConfirmationEnabled: boolean;
  isParentRequestConfirmationEnabled: boolean;
  onConfirmStudentRequest: () => void;
  onConfirmStaffRequest: () => void;
  onConfirmParentRequest: () => void;
  onBackStudentRequest: () => void;
  onBackStaffRequest: () => void;
  onBackParentRequest: () => void;
  onContinueAutoLink: () => void;
  onClearSelectedSchool: () => void;
}
interface State {}

export class Permission extends React.Component<Props, State> {
  render() {
    const {
      permission,
      permissionSelection,
      onPermissionClick,
      onSubmitStudentPermission,
      onSubmitStaffPermission,
      onSubmitParentPermission,
      onSubmitParentDetails,
      onBackToPermissionSelector,
      onBackToSchoolPicker,
      onBackFromParentDetails,
      parentDetailsDraft,
      initialOneOffCode,
      phone,
      isStaffRoleSelectorEnabled,
      isParentDetailsEnabled,
      isAutoLinkSuccessScreenEnabled,
      autoLinkedStudents,
      school,
      onStaffClick,
      staff,
      isFirstStaffTrialConfirmationEnabled,
      onConfirmFirstStaffTrial,
      onClearFirstStaffSchool,
      firstStaffTrialError,
      isStudentRequestConfirmationEnabled,
      isStaffRequestConfirmationEnabled,
      isParentRequestConfirmationEnabled,
      onConfirmStudentRequest,
      onConfirmStaffRequest,
      onConfirmParentRequest,
      onBackStudentRequest,
      onBackStaffRequest,
      onBackParentRequest,
      onContinueAutoLink,
      onClearSelectedSchool
    } = this.props;

    const isSchoolAccountInactive =
      school &&
      permission !== REGISTRATION_PERMISSION.STAFF &&
      propz.get(school, ['stats', 'rolesExistence', 'staff']) === false;

    if (isSchoolAccountInactive) {
      return <RegistrationSchoolUnavailable school={school} onBack={onClearSelectedSchool} />;
    }

    switch (permission) {
      case REGISTRATION_PERMISSION.STUDENT:
        if (isStudentRequestConfirmationEnabled) {
          return (
            <RegistrationRequestConfirmation
              schoolName={school && school.name ? school.name : 'your school'}
              roleLabel="Student"
              title="Check your student request"
              lines={[
                <>A member of school staff will check this request against the school roll.</>,
                <>If you supplied a school code, the request may be approved immediately.</>,
                <>We’ll let you know as soon as the school accepts or declines it.</>
              ]}
              onConfirm={onConfirmStudentRequest}
              onBack={onBackStudentRequest}
            />
          );
        }
        return (
          <div className={'mt-3'}>
            <StudentPermission
              phone={phone}
              onSubmit={onSubmitStudentPermission}
              onBack={onBackToPermissionSelector}
              initialSchool={school}
              initialOneOffCode={initialOneOffCode}
            />
          </div>
        );
      case REGISTRATION_PERMISSION.PARENT:
        if (isAutoLinkSuccessScreenEnabled) {
          return (
            <div className={'mt-3'}>
              <ParentAutoLinkSuccess
                school={school}
                linkedStudents={autoLinkedStudents}
                onContinue={onContinueAutoLink}
              />
            </div>
          );
        }
        if (isParentDetailsEnabled) {
          return (
            <div className={'mt-3'}>
              <ParentDetails
                school={school}
                onSubmit={onSubmitParentDetails}
                onBack={onBackFromParentDetails}
                initialStudents={parentDetailsDraft}
                isRequestReviewEnabled={isParentRequestConfirmationEnabled}
                onConfirmRequest={onConfirmParentRequest}
                onBackFromReview={onBackParentRequest}
              />
            </div>
          );
        } else if (isParentRequestConfirmationEnabled) {
          return (
            <RegistrationRequestConfirmation
              schoolName={school && school.name ? school.name : 'your school'}
              roleLabel="Parent or guardian"
              title="Check your parent request"
              lines={[
                <>The school will check your details before giving you access to a child.</>,
                <>Nothing in the child’s account becomes visible until the school approves the match.</>,
                <>We’ll let you know as soon as the request is accepted or declined.</>
              ]}
              onConfirm={onConfirmParentRequest}
              onBack={onBackParentRequest}
            />
          );
        } else {
          return (
            <div className={'mt-3'}>
              <ParentPermission
                phone={phone}
                onSubmit={onSubmitParentPermission}
                onBack={onBackToPermissionSelector}
                initialSchool={school}
                initialOneOffCode={initialOneOffCode}
              />
            </div>
          );
        }

      case REGISTRATION_PERMISSION.STAFF:
        if (isFirstStaffTrialConfirmationEnabled) {
          return (
            <FirstStaffTrialConfirmation
              school={school}
              onConfirm={onConfirmFirstStaffTrial}
              onClear={onClearFirstStaffSchool}
              error={firstStaffTrialError}
            />
          );
        }
        if (isStaffRequestConfirmationEnabled) {
          return (
            <RegistrationRequestConfirmation
              schoolName={school && school.name ? school.name : 'your school'}
              roleLabel={getStaffRoleLabel(staff)}
              title="Check your staff request"
              lines={[
                <>An existing school admin will review this request before your access opens.</>,
                <>Your requested role controls which school tools and information you can use.</>,
                <>We’ll let you know as soon as the request is accepted or declined.</>
              ]}
              onConfirm={onConfirmStaffRequest}
              onBack={onBackStaffRequest}
            />
          );
        }
        if (isStaffRoleSelectorEnabled) {
          return (
            <div className={'mt-3'}>
              <StaffRoleSelector
                staff={staff}
                school={school}
                onStaffClick={onStaffClick}
                onBack={onBackToSchoolPicker}
              />
            </div>
          );
        } else {
          return (
            <div className={'mt-3'}>
              <StaffPermission
                phone={phone}
                onSubmit={onSubmitStaffPermission}
                onBack={onBackToPermissionSelector}
                initialSchool={school}
                initialOneOffCode={initialOneOffCode}
              />
            </div>
          );
        }
      default:
        return (
          <div className={'mt-3'}>
            <RegistrationPermissionSelector permission={permissionSelection} onPermissionClick={onPermissionClick} />
          </div>
        );
    }
  }
}

function FirstStaffTrialConfirmation(props: {
  school: any;
  onConfirm: (trialModules: string[]) => void;
  onClear: () => void;
  error?: string;
}) {
  const schoolName = props.school && props.school.name ? props.school.name : 'your school';
  const [selectedModules, setSelectedModules] = React.useState<string[]>([]);
  const modules = [
    { key: 'SPORT', title: 'Sport & events', description: 'Fixtures, teams, availability, attendance and results.' },
    {
      key: 'CLUBS',
      title: 'Clubs & activities',
      description: 'Programmes, bookings, capacity, waiting lists and payments.'
    },
    {
      key: 'COMMUNICATIONS',
      title: 'Communications',
      description: 'Messages, event chats, reminders and parent engagement.'
    },
    { key: 'AWARDS', title: 'Awards', description: 'Recognise participation and achievement across the school.' }
  ];

  const toggleModule = (moduleKey: string) => {
    setSelectedModules(current =>
      current.includes(moduleKey) ? current.filter(key => key !== moduleKey) : [...current, moduleKey]
    );
  };

  return (
    <div className="eFirstStaffTrial">
      <div className="eSchoolSignupScreenHeader">
        <h1>Your school</h1>
        <p>Check the school carefully before starting its Squad In Touch account.</p>
      </div>

      <section className="eFirstStaffTrialPanel">
        <header>
          <strong>You're first here — start the school's trial</strong>
          <p>
            Nobody from <b>{schoolName}</b> has an account yet, so you set the school up.
          </p>
        </header>
        <div className="eFirstStaffTrialBody">
          {props.error && <div className="eFirstStaffTrialError">{props.error}</div>}
          <ul>
            <li>
              You confirm you are an authorised representative of <strong>{schoolName}</strong>.
            </li>
            <li>
              The school's <strong>30-day free trial</strong> starts today. No card, nothing to cancel.
            </li>
            <li>
              You get the <strong>School Admin</strong> role and can invite colleagues straight away.
            </li>
          </ul>
          <div className="eFirstStaffTrialModules" aria-label="Choose trial modules">
            <h2>What would you like to try?</h2>
            <p>Select at least one module. You can add other modules later from the Admin console.</p>
            <div className="eFirstStaffTrialModulesGrid">
              {modules.map(module => {
                const isSelected = selectedModules.includes(module.key);
                return (
                  <button
                    type="button"
                    key={module.key}
                    className={`eFirstStaffTrialModule ${isSelected ? 'isSelected' : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => toggleModule(module.key)}
                  >
                    <span className="eFirstStaffTrialModuleCheck" aria-hidden="true">
                      {isSelected ? '✓' : ''}
                    </span>
                    <strong>{module.title}</strong>
                    <small>{module.description}</small>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="eFirstStaffTrialActions">
            <button
              type="button"
              className="btn btn-primary eRegistrationSubmitButton"
              onClick={() => props.onConfirm(selectedModules)}
              disabled={selectedModules.length === 0}
            >
              Start {selectedModules.length || ''} selected trial{selectedModules.length === 1 ? '' : 's'}{' '}
              <span aria-hidden="true">→</span>
            </button>
            <button type="button" className="btn btn-secondary" onClick={props.onClear}>
              Back
            </button>
          </div>
          <p className="eFirstStaffTrialHint">
            Not the right person to set this up? Ask an authorised colleague to do it, then join as staff once they
            have.
          </p>
        </div>
      </section>
    </div>
  );
}

// ─── Auto-link success screen ─────────────────────────────────────────────────

interface AutoLinkSuccessProps {
  school: any;
  linkedStudents: Array<{ studentId: string; studentName: string }>;
  onContinue: () => void;
}

function ParentAutoLinkSuccess({ school, linkedStudents, onContinue }: AutoLinkSuccessProps) {
  const schoolName = school && school.name ? school.name : 'your school';

  const isSingle = linkedStudents.length === 1;

  return (
    <div className="eAutoLinkSuccess mt-3">
      <div className="eAutoLinkSuccessIcon">✓</div>

      <h3 className="eAutoLinkSuccessTitle">
        {isSingle ? "You've been linked to a child!" : "You've been linked to your children!"}
      </h3>

      <p className="eAutoLinkSuccessIntro">
        We found your details in <strong>{schoolName}</strong>'s records and automatically granted you access to the
        following {isSingle ? 'child' : 'children'}:
      </p>

      <ul className="eAutoLinkSuccessList">
        {linkedStudents.map(s => (
          <li key={s.studentId} className="eAutoLinkSuccessListItem">
            {s.studentName || 'Student'}
          </li>
        ))}
      </ul>

      <p className="eAutoLinkSuccessHint">
        If you need access to additional children, you can request it from your <strong>profile page</strong> once your
        registration is complete.
      </p>

      <p className="eAutoLinkSuccessHint">
        Your registration is complete and the child is already available in your account.
      </p>

      <button type="button" className="btn btn-primary" onClick={onContinue}>
        Go to my calendar
      </button>
    </div>
  );
}

function getStaffRoleLabel(staff: RegistrationStaff): string {
  switch (staff) {
    case 'ADMIN':
      return 'School admin';
    case 'MANAGER':
      return 'Manager';
    case 'COACH':
      return 'Coach';
    case 'TEACHER':
      return 'Teacher';
    default:
      return 'Member of staff';
  }
}

function RegistrationSchoolUnavailable(props: { school: any; onBack: () => void }) {
  const schoolName = propz.get(props.school, ['name'], 'This school');
  const schoolEmail = String(
    propz.get(props.school, ['email'], '') || propz.get(props.school, ['sportsDepartmentEmail'], '') || ''
  ).trim();
  const [copyStatus, setCopyStatus] = React.useState<'idle' | 'copied' | 'error'>('idle');
  const shareMessage =
    `I’d like ${schoolName} to use Squad In Touch for school communications, clubs, activities, sport and events. ` +
    'Schools can learn more and start a 30-day trial at https://app.squadintouch.com/registration.';

  const copyShareMessage = async () => {
    let textArea: HTMLTextAreaElement | undefined;

    try {
      const clipboard = (navigator as any).clipboard;

      if (clipboard && clipboard.writeText) {
        await clipboard.writeText(shareMessage);
      } else {
        textArea = document.createElement('textarea');
        textArea.value = shareMessage;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();

        if (!document.execCommand('copy')) {
          throw new Error('Copy command was not available');
        }
      }

      setCopyStatus('copied');
    } catch (error) {
      setCopyStatus('error');
    } finally {
      if (textArea && textArea.parentNode) {
        textArea.parentNode.removeChild(textArea);
      }
    }
  };

  return (
    <div className="eRegistrationSchoolUnavailable">
      <div className="eSchoolSignupScreenHeader">
        <h1>{schoolName} hasn’t activated Squad In Touch yet</h1>
        <p>You can keep your account and connect it to the school once its Squad In Touch account is active.</p>
      </div>

      <section>
        <div className="eRegistrationSchoolUnavailableSummary">
          <span aria-hidden="true">i</span>
          <div>
            <strong>No school access can be requested yet</strong>
            <p>
              Parent and student access is approved by the school. You won’t need to register again when the school
              activates its account.
            </p>
          </div>
        </div>

        <div className="eRegistrationSchoolUnavailableActions">
          <button type="button" className="btn btn-primary" onClick={props.onBack}>
            Search for another school
          </button>
          <button type="button" className="btn btn-secondary" onClick={copyShareMessage}>
            {copyStatus === 'copied' ? 'Message copied' : 'Copy a message for the school'}
          </button>
        </div>

        <div className="eRegistrationSchoolUnavailableShare" role="status" aria-live="polite">
          {copyStatus === 'copied' ? (
            <React.Fragment>
              <strong>Ready to share</strong>
              <span>
                Paste the message into an email to {schoolEmail ? <b>{schoolEmail}</b> : 'the school office'}.
              </span>
            </React.Fragment>
          ) : copyStatus === 'error' ? (
            <React.Fragment>
              <strong>Copy wasn’t available</strong>
              <span>Select the message below and copy it manually.</span>
              <textarea readOnly={true} value={shareMessage} onFocus={event => event.currentTarget.select()} />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <strong>Want to let the school know?</strong>
              <span>Copy a short ready-made message and send it to the school office.</span>
            </React.Fragment>
          )}
        </div>
      </section>
    </div>
  );
}
